import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { Sfx } from "../../b2b/ui/Sfx";
import { LOOK } from "../../b2c/look";
import { StoreBadge } from "../../b2c/ui/bits";
import { Words } from "../../b2c/ui/Words";
import { alpha, mix } from "../color";
import { useMerchant } from "../copy";
import type { SceneProps } from "../timing";
import { useStage } from "../stage";
import { BEAT } from "../timing";
import { FOOT_ROOM, footnoteBox } from "../ui/Footnote";
import { LogoTile } from "../ui/Logo";

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;

/**
 * Drawn, not typed: "→" is outside the latin subset of Inter we load, so
 * as a glyph it would depend on the render machine's fallback fonts.
 */
const Arrow: React.FC<{ size: number; color: string }> = ({ size, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M4 12h15m-6-6 6 6-6 6"
      stroke={color}
      strokeWidth={2.6}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * End card — the brand's logo tile wearing a small Frak badge ("recommend
 * it with Frak"), the brand page's promise ("Recommend {name}, earn up to
 * {reward}"), and where to go: the brand's own site (where the Frak share
 * button lives), or the app. Settles early and holds, so a paused last
 * frame reads as a finished CTA and the loop back to the photo is clean.
 */
export const End: React.FC<SceneProps> = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const { m, t, copy } = useMerchant();
  const stage = useStage();
  const c = m.colors;

  // The bumper's end card is ~half as long: everything lands faster.
  const k = durationInFrames < 6 * BEAT ? 0.6 : 1;
  const at = (f: number) => Math.round(f * k);

  const wide = stage.shape === "wide";
  const G = {
    tall: { logo: 260, head: 96, sub: 64, cta: 40, gap: 34 },
    feed: { logo: 200, head: 84, sub: 56, cta: 36, gap: 26 },
    square: { logo: 150, head: 70, sub: 48, cta: 32, gap: 18 },
    wide: { logo: 340, head: 100, sub: 64, cta: 38, gap: 30 },
  }[stage.shape];

  const logo = spring({
    frame: frame - 1,
    fps,
    config: { damping: 11, stiffness: 200, mass: 0.7 },
  });
  const cta = spring({
    frame: frame - at(24),
    fps,
    config: { damping: 14, stiffness: 200, mass: 0.7 },
  });
  const stores = spring({
    frame: frame - at(32),
    fps,
    config: { damping: 16, stiffness: 190 },
  });
  const sheen = interpolate(frame, [at(40), at(58)], [-30, 130], clamp);
  const bg = mix(c.brand, "#ffffff", 0.9);

  const ctaText = m.site ? t(copy.end.cta) : t(copy.end.ctaNoSite);

  const text = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: wide ? "flex-start" : "center",
        gap: G.gap * 0.5,
        maxWidth: wide ? 1000 : stage.w - stage.safe.left - stage.safe.right,
      }}
    >
      <Words
        text={t(copy.end.head)}
        start={at(6)}
        fontSize={G.head}
        color={LOOK.ink}
        align={wide ? "left" : "center"}
      />
      <Words
        text={t(copy.end.reward)}
        start={at(12)}
        stagger={2}
        fontSize={G.sub}
        color={c.onWhite}
        align={wide ? "left" : "center"}
      />
      <div
        style={{
          position: "relative",
          overflow: "hidden",
          marginTop: G.gap,
          padding: `${G.cta * 0.62}px ${G.cta * 1.1}px`,
          borderRadius: 999,
          background: c.brand,
          color: c.onBrand,
          fontFamily: LOOK.display,
          fontWeight: LOOK.bold,
          fontSize: G.cta,
          letterSpacing: -0.5,
          boxShadow: `0 24px 50px -18px ${alpha(mix(c.brand, "#000000", 0.4), 0.7)}`,
          scale: cta,
          whiteSpace: "nowrap",
          display: "flex",
          alignItems: "center",
          gap: G.cta * 0.35,
        }}
      >
        {ctaText}
        <Arrow size={G.cta * 0.8} color={c.onBrand} />
        <div
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: `${sheen}%`,
            width: "18%",
            background:
              "linear-gradient(90deg, transparent, rgba(255,255,255,0.45), transparent)",
            rotate: "20deg",
          }}
        />
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 20,
          marginTop: G.gap * 0.5,
          opacity: stores,
          translate: `0 ${(1 - stores) * 20}px`,
          fontFamily: LOOK.body,
          fontWeight: LOOK.medium,
          fontSize: G.cta * 0.8,
          color: LOOK.inkSoft,
          flexWrap: "wrap",
          justifyContent: wide ? "flex-start" : "center",
        }}
      >
        <span>{copy.end.or}</span>
        <div
          style={{
            display: "flex",
            gap: 14,
            scale: G.cta / 46,
            transformOrigin: wide ? "0% 50%" : "50% 50%",
          }}
        >
          <StoreBadge
            store="apple"
            small={copy.end.appStoreSmall}
            name={copy.end.appStore}
          />
          <StoreBadge
            store="google"
            small={copy.end.playSmall}
            name={copy.end.play}
          />
        </div>
      </div>
    </div>
  );

  return (
    <AbsoluteFill
      style={{
        background: `radial-gradient(110% 80% at 50% 30%, #ffffff 0%, ${bg} 70%)`,
      }}
    >
      <AbsoluteFill
        style={{
          flexDirection: wide ? "row" : "column",
          alignItems: "center",
          justifyContent: "center",
          gap: wide ? 110 : G.gap * 1.3,
          padding: `${stage.safe.top}px ${stage.safe.right}px ${stage.safe.bottom + FOOT_ROOM[stage.shape]}px ${stage.safe.left}px`,
        }}
      >
        <LogoTile
          m={m}
          size={G.logo}
          badge
          style={{ scale: logo, rotate: `${(1 - logo) * -8}deg` }}
        />
        {text}
      </AbsoluteFill>
      <div
        style={{
          ...footnoteBox(stage),
          color: LOOK.inkSoft,
        }}
      >
        {copy.footnote}
      </div>
      <Sfx sound="pop" at={1} volume={0.8} />
      <Sfx sound="sparkle" at={at(44)} volume={0.7} />
    </AbsoluteFill>
  );
};
